import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Loader2Icon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { FormFieldControl } from './FormFieldControl';
import { FormSuccess } from './FormSuccess';
import { PlanChip } from './PlanChip';
import { useFormCard } from './useFormCard';
import { isRequired } from '../../utils/formValidation';
import type { FormPreviewState, FormVariant } from '../../types/form';
type FormCardProps = {
  variant: FormVariant;
  preview: FormPreviewState;
};
export function FormCard({
  variant,
  preview
}: FormCardProps) {
  const {
    values,
    errors,
    status,
    idPrefix,
    visibleFields,
    setValue,
    blur,
    submit
  } = useFormCard(variant, preview);
  const reduceMotion = useReducedMotion();
  const {
    form
  } = variant;
  const loading = status === 'loading';
  const errorCount = Object.keys(errors).length;
  return <div data-id="element-425" data-mp="31nx2b3s3abm3">
      <section aria-labelledby={`${idPrefix}-title`} className="rounded-[14px] border border-canvas-line bg-white p-6 shadow-[0_24px_48px_-32px_rgba(11,11,11,0.28)] sm:p-8" data-id="element-426" data-mp="31nonv7gbiy8s">
        {status === 'success' ? <FormSuccess success={variant.success} data-id="element-427" data-mp="3tvwdl9hzywcn" /> : <>
            {form.showPlanChip && <PlanChip data-id="element-428" data-mp="bhn38jx97xie" />}
            <div className="flex items-baseline justify-between gap-4" data-id="element-429" data-mp="bi6ooam45416">
              <h2 id={`${idPrefix}-title`} className="text-[22px] font-semibold tracking-[-0.02em] text-ink" data-id="element-430" data-mp="6oc5r9ec0t35">
                {form.title}
              </h2>
              <p className="shrink-0 text-[13px] text-ink-muted" data-id="element-431" data-mp="gfygl4mrcq5d">
                <span className="text-brand" aria-hidden="true" data-id="element-432" data-mp="ghswu5n33u34">
                  *
                </span>{' '}
                Required
              </p>
            </div>
            <p className="mt-1 text-[14px] text-ink-muted" data-id="element-433" data-mp="qee5d0ybbnbp">{form.subtitle}</p>

            <div aria-live="polite" className="sr-only" data-id="element-434" data-mp="2w7cgwtm81d9i">
              {errorCount > 0 ? `${errorCount} ${errorCount === 1 ? 'field needs' : 'fields need'} attention.` : ''}
            </div>

            <form noValidate onSubmit={submit} className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-435" data-mp="v5emifhgaodk">
              <AnimatePresence initial={false} data-id="element-436" data-mp="104hkxh2xdwck">
                {visibleFields.map(field => <motion.div key={field.name} layout={reduceMotion ? false : 'position'} initial={field.showIf && !reduceMotion ? {
              opacity: 0,
              y: -4
            } : false} animate={{
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className={field.half ? 'sm:col-span-1' : 'sm:col-span-2'} data-id="element-437" data-mp="151hs5vbih559">
                    <FormFieldControl field={field} idPrefix={idPrefix} values={values} required={isRequired(field, values)} error={errors[field.name]} onChange={setValue} onBlur={blur} data-id="element-438" data-mp="1su6jzow2rmbj" />
                  </motion.div>)}
              </AnimatePresence>

              <div className="pt-1 sm:col-span-2" data-id="element-439" data-mp="3d9mjvtaalxle">
                <button type="submit" disabled={loading} aria-busy={loading} className="flex h-12 w-full items-center justify-center gap-2 rounded-[7px] bg-brand px-5 text-[16px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover disabled:cursor-wait disabled:bg-brand/80" data-id="element-440" data-mp="3daeccv8lff7g">
                  {loading && <Loader2Icon className="h-[18px] w-[18px] animate-spin" aria-hidden="true" data-id="element-441" data-mp="19ome5jfkxy" />}
                  {loading ? form.loadingLabel : form.submitLabel}
                </button>
                <p className="mt-3 text-center text-[13px] text-ink-muted" data-id="element-442" data-mp="sl8bjnuf5xni">
                  By submitting, you agree to our{' '}
                  <SmartLink href="/legal/privacy" className="font-medium text-ink underline underline-offset-2 hover:text-brand" data-id="element-443" data-mp="8bfffxnywm8c">
                    Privacy Policy
                  </SmartLink>
                  .
                </p>
                {form.afterSubmit && <p className="mt-5 border-t border-canvas-line pt-5 text-center text-[14px] text-ink-muted" data-id="element-444" data-mp="3ukhzhwjkfp00">
                    {form.afterSubmit.text}{' '}
                    <SmartLink href={form.afterSubmit.href} className="font-semibold text-brand transition-colors duration-150 ease-out hover:text-brand-hover" data-id="element-445" data-mp="r2zvk06wz24v">
                      {form.afterSubmit.linkLabel}
                    </SmartLink>
                  </p>}
              </div>
            </form>
          </>}
      </section>

      {form.disclosure && <p className="mt-4 rounded-[12px] border border-canvas-line bg-white px-5 py-4 text-[12px] leading-[1.6] text-ink-muted" data-id="element-446" data-mp="1er9bilhg4vo3">
          {form.disclosure}
        </p>}
    </div>;
}